import AuthFormBtn from '@components/AuthForm/components/AuthFormBtn';
import { Picture } from '@ui';
import icons from '@icons';
import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import useWindowSize from '@customHooks/useWindowSize';

interface AuthFormMaxLoginProps {
  link: string;
  ttl: number;
  error: string;
  status?: string;
  onBack: () => void;
  onRetry: () => void;
  onConfirm: () => void | Promise<void>;
}

const {
  arrowIosLeft: { midGrey: arrowLeftMidGrey }
} = icons.Outline;

const AuthFormMaxLogin: FC<AuthFormMaxLoginProps> = ({
  link,
  ttl,
  error,
  status,
  onBack,
  onRetry,
  onConfirm
}) => {
  const [secondsLeft, setSecondsLeft] = useState(ttl);
  const [isChecking, setIsChecking] = useState(false);
  const { isMobile } = useWindowSize();

  useEffect(() => {
    setSecondsLeft(ttl);
    const timer = setInterval(() => {
      setSecondsLeft(seconds => Math.max(seconds - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [ttl]);

  const openMax = useCallback((): void => {
    if (isMobile) {
      window.location.assign(link);
      return;
    }

    window.open(link, '_blank');
  }, [isMobile, link]);

  const handleConfirm = useCallback(async (): Promise<void> => {
    if (isChecking) {
      return;
    }
    setIsChecking(true);
    try {
      await onConfirm();
    } finally {
      setIsChecking(false);
    }
  }, [isChecking, onConfirm]);

  if (error) {
    return (
      <div className='w-full'>
        <div className='AuthForm__heading'>Не удалось войти через MAX</div>
        <p className='mb-6 text-center text-sm text-[var(--dark-red)]'>
          {error}
        </p>
        <AuthFormBtn onClick={onRetry}>Начать заново</AuthFormBtn>
      </div>
    );
  }

  return (
    <div className='w-full'>
      <div className='AuthForm__heading-row'>
        <Picture
          icon={arrowLeftMidGrey}
          onClick={onBack}
          height='24'
          width='24'
          className='picture-component picture-component--on-click'
        />
        <div className='AuthForm__heading'>Подтвердите вход в MAX</div>
      </div>
      <p className='mb-6 text-center text-sm'>
        Откройте MAX и нажмите «Начать». При первом входе бот попросит
        поделиться номером телефона.
      </p>
      <AuthFormBtn type='button' className='mb-3' onClick={openMax}>
        Открыть MAX
      </AuthFormBtn>
      <AuthFormBtn
        type='button'
        className='AuthFormBtn--grey mb-3'
        disabled={isChecking}
        onClick={() => {
          void handleConfirm();
        }}
      >
        {isChecking ? 'Проверяем…' : 'Я подтвердил в MAX'}
      </AuthFormBtn>
      {status ? (
        <p className='mb-3 text-center text-sm text-[var(--ultra-dark-grey)]'>
          {status}
        </p>
      ) : null}
      <p className='text-center text-xs text-[var(--ultra-dark-grey)]'>
        Ожидаем подтверждения{secondsLeft > 0 && ` (${secondsLeft} сек.)`}
      </p>
    </div>
  );
};

export default AuthFormMaxLogin;
